/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/* 
    Hello there! This is a demonstration of how to override a file from the base template.
    
    It's necessary that the module export interface remain consistent, 
    as other files in the base template rely on constants.js, thus we
    import the underlying constants.js, modifies it and re-export it.
*/

import {defineMessage} from 'react-intl'
import {
    DEFAULT_LIMIT_VALUES,
    DEFAULT_SEARCH_PARAMS,
    cssColorGroups
} from '@salesforce/retail-react-app/app/constants'

// original value is 25
DEFAULT_LIMIT_VALUES[0] = 30
DEFAULT_SEARCH_PARAMS.limit = 90
delete DEFAULT_SEARCH_PARAMS.sort // to use BM default sort we are deleting sort from default params

// Colors values
cssColorGroups.red = '#AE122A'
cssColorGroups.brown = '#51372A'
cssColorGroups.blue = '#556AA7'
cssColorGroups.pink = '#FBCECE'
cssColorGroups.silver = 'linear-gradient(239deg, #FCFBFB 19.16%, #898989 94.9%)'
cssColorGroups.grey = '#D9D9D9'
cssColorGroups.yellow = '#FFD67D'
cssColorGroups.green = '#59804D'
cssColorGroups.miscellaneous =
    'linear-gradient(270deg, #3B559A 0%, #3AB446 23.5%, #F7FF58 50.5%, #E69E21 77%, #AE122A 100%)'
cssColorGroups.copper = '#B87333'

export const CUSTOM_HOME_TITLE = '🎉 Hello Extensible React Template!'
export const MOBILE_NAV_INTIAL_DEATURE_IMAGE = 'static/img/header/mobile-main-feature.webp'
export const DEFAULT_SITE_TITLE = 'Cavenders'
export const CAT_MENU_NAV_LINK_LABEL = 3
export const CAT_MENU_DEFAULT_ROOT_CATEGORY = 'root'
export const SITEMAP_CAVENDERS_LABEL = `Cavender's`
export const GET_STORE_LIMIT = 1
export const GET_STORE_MAX_DISTANCE = 75

export const PDP_RECOMMENDATION = 'pdp'
export const PDP_RECENTLY_VIEW = 'recently-viewed'
export const RECOMMENDED_FOR_YOU = 'best-sellers-null-results'
export const CART_RECOMMENDATION = 'cart'
export const CHECKOUT_PAYPAL = 'PayPal'
export const CHECKOUT_GIFT_CARD = 'GIFT_CERTIFICATE'

export const CART_LIMIT_ERROR_CODE = 'SYS-1007'
export const CART_FULL_ERROR_MESSAGE =
    'Your Cart is full! Please head to checkout or remove a few items to keep shopping.'

export const ERROR_MESSAGE_TIMEOUT = 5000

export const PASSWORD_RESET_ERROR_MESSAGE = defineMessage({
    id: 'reset_password.error.something_went_wrong',
    defaultMessage: 'Something went wrong. Try again!'
})

export {DEFAULT_LIMIT_VALUES, DEFAULT_SEARCH_PARAMS}

export * from '@salesforce/retail-react-app/app/constants'

export const refreshPageForCategoryFilters = [
    '/collections',
    '/rodeo',
    '/jrc-and-sons-collection',
    '/outerwear',
    '/men-southwest-collection',
    '/women-southwest-collection',
    '/kids-southwest-collection',
    '/lady-luxe-get-the-look',
    '/mens-workwear',
    '/work-boots-mens',
    '/women-jrc-and-sons',
    '/accessories-women-western-jewelry',
    '/belts-buckles',
    '/ariat',
    '/ariat-boots',
    '/ariat-men',
    '/ariat-womens',
    '/ariat-kids',
    '/ariat-work',
    '/ariat-collections',
    '/ariat-venttek-boots-clothing',
    '/consuela',
    '/corral-boots',
    '/cowboy-workwear',
    '/JRCandSons-Mens',
    '/JRCandSons-Mens-Boots',
    '/JRCandSons-Womens',
    '/JRCandSons-Womens-Boots',
    '/JRCandSons-Boots',
    '/r-watson',
    '/rafter-c',
    '/red-dirt-hat-co',
    '/rock-and-roll-denim',
    '/rock-and-roll-denim-mens',
    '/rock-and-roll-denim-womens',
    '/rock-and-roll-denim-boys',
    '/rock-and-roll-denim-girls',
    '/rockin-c',
    '/mens-wrangler-brand',
    '/womens-wrangler-brand',
    '/boys-wrangler-brand',
    '/girls-wrangler-brand',
    '/wrangler-all-workwear',
    '/wrangler-collections',
    '/wrangler-retro-collection',
    '/wrangler-cowboy-cut-collection',
    '/all-fragrance-1',
    '/home-decor',
    '/knives-and-sheaths-3',
    '/wrangler-wednesday',
    '/wrangler-20x-collection',
    '/wrangler-george-strait-collection',
    '/gifts',
    '/kids-clothing',
    '/mens-womens-tshirts'
]

//Constants for customer orders searching.
export const DEFAULT_ORDERS_SEARCH_PARAMS = {limit: 50, offset: 0, sort: 'best-matches', refine: []}

//Constants for customer blogs searching.
export const DEFAULT_BLOGS_SEARCH_PARAMS = {sz: 10, start: 0, sort: 'newest'} // As per production
export const BLOGS_SEARCH_SORT_OPTIONS = ['newest', 'oldest', 'name-ascending', 'name-descending']
export const BLOGS_SEARCH_PER_PAGE_OPTIONS = [10, 30]

//Month constants for blog archive
export const MONTH_NAMES_FULL = {
    1: 'January',
    2: 'February',
    3: 'March',
    4: 'April',
    5: 'May',
    6: 'June',
    7: 'July',
    8: 'August',
    9: 'September',
    10: 'October',
    11: 'November',
    12: 'December'
}

export const MONTH_NUMBERS = {
    January: 1,
    February: 2,
    March: 3,
    April: 4,
    May: 5,
    June: 6,
    July: 7,
    August: 8,
    September: 9,
    October: 10,
    November: 11,
    December: 12
}

export const MONTH_NAMES_ABBR = {
    Jan: 'January',
    Feb: 'February',
    Mar: 'March',
    Apr: 'April',
    May: 'May',
    Jun: 'June',
    Jul: 'July',
    Aug: 'August',
    Sep: 'September',
    Oct: 'October',
    Nov: 'November',
    Dec: 'December'
}
// Menu default colors (list-menu and mega-menu)
export const MENU_DEFAULT_TEXT_COLOR = 'black'
export const MENU_DEFAULT_HOVER_COLOR = 'cav_red'

// Mega menu column distribution constants
export const MEGA_MENU_HEADING_HEIGHT_PX = 34
export const MEGA_MENU_SUBCATEGORY_HEIGHT_PX = 29
export const MEGA_MENU_GROUP_GAP_PX = 40
export const MEGA_MENU_CONTAINER_PADDING_PX = 80
export const MEGA_MENU_MAX_CONTAINER_HEIGHT_PX = 470
export const MEGA_MENU_MAX_COLUMN_HEIGHT_PX =
    MEGA_MENU_MAX_CONTAINER_HEIGHT_PX - MEGA_MENU_CONTAINER_PADDING_PX
export const MEGA_MENU_MAX_ALLOWED_COLUMN_HEIGHT_PX = 500
export const MEGA_MENU_CONTENT_SLOT_ID = 'megamenu-slot:category:'
export const MEGA_MENU_MAXIMUM_NUMBER_COLUMNS = 4

export const DRAWER_SOCIAL_ICONS = [
    {
        src: 'static/img/header/instagram.png',
        alt: 'Instagram',
        url: 'https://www.instagram.com/cavenders'
    },
    {
        src: 'static/img/header/facebook.png',
        alt: 'Facebook',
        url: 'https://www.facebook.com/cavenders'
    },
    {
        src: 'static/img/header/linkedin.png',
        alt: 'LinkedIn',
        url: 'https://www.linkedin.com/company/cavender%27s/'
    },
    {
        src: 'static/img/header/youtube.png',
        alt: 'YouTube',
        url: 'https://www.youtube.com/channel/UCB0fl3kc_Rxes2thA-FqX8w'
    },
    {
        src: 'static/img/header/pinterest.png',
        alt: 'Pinterest',
        url: 'https://www.pinterest.com/cavenders/'
    },
    {
        src: 'static/img/header/cavenders.png',
        alt: 'Cavenders',
        url: 'https://www.cavenders.com/blog/cavenders-ranch/'
    }
]

// Constants for shopper context
// Supported non-string field types used in SHOPPER_CONTEXT_SEARCH_PARAMS below.
// Only non-string types need to be identified using the "type" field.
// If no "type" field is present, the value will be parsed as a string by default.
export const SHOPPER_CONTEXT_FIELD_TYPES = {
    INT: 'int',
    DOUBLE: 'double',
    ARRAY: 'array'
}
export const SHOPPER_CONTEXT_SEARCH_PARAMS = {
    sourceCode: {paramName: 'src'},
    geoLocation: {
        city: {paramName: 'city'},
        country: {paramName: 'country'},
        countryCode: {paramName: 'countryCode'},
        latitude: {paramName: 'latitude', type: SHOPPER_CONTEXT_FIELD_TYPES.DOUBLE},
        longitude: {paramName: 'longitude', type: SHOPPER_CONTEXT_FIELD_TYPES.DOUBLE},
        metroCode: {paramName: 'metroCode'},
        postalCode: {paramName: 'postalCode'},
        region: {paramName: 'region'},
        regionCode: {paramName: 'regionCode'}
    },
    customQualifiers: {
        // Add custom qualifiers here
    },
    assignmentQualifiers: {
        // Add assignment qualifiers here
    }
}

export const X_FORWARDED_FOR_HEADER = 'x-forwarded-for'
export const CLIENT_IP_INFO_URL = '/api/client/clientIpInfo'
export const CONTENT_TYPE = {
    JSON: 'application/json'
}
